// components/access-guards.tsx
import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useOrgStore } from '@/stores/orgStore'

const UserLevelContext = React.createContext<number | null>(null)

export const AccessProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUserLevel } = useOrgStore()
  return (
    <UserLevelContext.Provider value={currentUserLevel}>
      {children}
    </UserLevelContext.Provider>
  )
}

const useUserLevel = () => {
  const context = React.useContext(UserLevelContext)
  if (context === undefined) {
    throw new Error('useUserLevel must be used within a AccessProvider')
  }
  return context
}

const createGuard = (requiredLevel: number) => {
  const GuardComponent = ({
    children,
    redirect = false,
  }: {
    children: React.ReactNode
    redirect?: boolean
  }) => {
    const userLevel = useUserLevel()
    const navigate = useNavigate()

    React.useEffect(() => {
      if (redirect && userLevel !== null && userLevel > requiredLevel) {
        navigate({ to: '/' })
      }
    }, [userLevel, navigate, redirect])

    // Completely remove from DOM if unauthorized
    if (userLevel === null || userLevel > requiredLevel) {
      return redirect ? null : null
    }

    return <>{children}</>
  }

  return GuardComponent
}

const createInvisibleGuard = (requiredLevel: number) => {
  const GuardComponent = ({
    children,
    redirect = false,
  }: {
    children: React.ReactNode
    redirect?: boolean
  }) => {
    const userLevel = useUserLevel()
    const navigate = useNavigate()

    React.useEffect(() => {
      if (redirect && userLevel !== null && userLevel > requiredLevel) {
        navigate({ to: '/' })
      }
    }, [userLevel, navigate, redirect])

    if (userLevel === null || userLevel > requiredLevel) {
      return redirect ? null : (
        <div className='fullhidden invisible absolute h-0 w-0'>{children}</div>
      )
    }

    return <>{children}</>
  }

  return GuardComponent
}

export const MasterGuard = createGuard(0)
export const AdminGuard = createGuard(1)
export const UserGuard = createGuard(2)
export const ExternalGuard = createGuard(3)

export const MasterGuardInvisible = createInvisibleGuard(0)
export const AdminGuardInvisible = createInvisibleGuard(1)
export const UserGuardInvisible = createInvisibleGuard(2)
export const ExternalGuardInvisible = createInvisibleGuard(3)

/*
Complete removal:
<AdminGuard>
  <SecretDashboard />
</AdminGuard>

Invisible version (preserves layout)
<AdminGuardInvisible>
  <div className="border p-4">
    <h2>Admin Controls</h2>
    <button>Dangerous action</button>
  </div>
</AdminGuardInvisible>


*/
